import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { DEFAULT_DARK_THEME, DEFAULT_LIGHT_THEME, EMPTY_CUSTOM_THEME, THEME_TYPES, getDefaultTheme } from '../constants/themeDefaults';
import { applyThemeToDOM, detectSystemPreference, watchSystemPreference } from '../utils/themeUtils';
import themeService from '../services/themeService';
import { companyApi } from '../lib/api-service';
import toast from 'react-hot-toast';
import ThemeSelectionModal from '../components/shared/ThemeSelectionModal';

export const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const STORAGE_KEY = 'theme-preference';
const USER_SET_PREFERENCE_KEY = 'user-set-theme-preference';

export function ThemeProvider({ children }) {
  const [currentThemeType, setCurrentThemeType] = useState(THEME_TYPES.DARK);
  const [customTheme, setCustomTheme] = useState(null);
  const [isAdminEnforced, setIsAdminEnforced] = useState(false);
  const [enforcedTheme, setEnforcedTheme] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasUserSetPreference, setHasUserSetPreference] = useState(false);
  const [hasBackendPreference, setHasBackendPreference] = useState(null); // null = not checked yet, true = has preference, false = no preference
  const [showThemeSelectionModal, setShowThemeSelectionModal] = useState(false);

  // Load initial theme from localStorage
  useEffect(() => {
    const loadInitialTheme = () => {
      try {
        // Check if user has explicitly set a preference
        const userSet = localStorage.getItem(USER_SET_PREFERENCE_KEY);
        setHasUserSetPreference(userSet === 'true');

        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
          const { type, custom } = JSON.parse(saved);
          setCurrentThemeType(type || THEME_TYPES.DARK);
          if (custom) {
            setCustomTheme(custom);
          }
        } else if (!userSet) {
          // Auto-detect system preference only if user hasn't set preference
          const systemPref = detectSystemPreference();
          setCurrentThemeType(systemPref);
        }
      } catch (error) {
        console.error('Failed to load theme from localStorage:', error);
        setCurrentThemeType(THEME_TYPES.DARK);
      }
    };

    loadInitialTheme();
  }, []);

  // Load theme from backend and check for enforcement (when backend is available)
  useEffect(() => {
    const loadBackendTheme = async () => {
      try {
        // First, check for organization mandatory theme
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (token) {
          try {
            const company = await companyApi.getCompany();
            if (company && company.mandatory_theme) {
              console.log('[ThemeProvider] Organization has mandatory theme');
              setIsAdminEnforced(true);
              setEnforcedTheme(company.mandatory_theme);
              setCurrentThemeType(company.mandatory_theme.type || THEME_TYPES.CUSTOM);
              setHasBackendPreference(true);

              // Save enforced theme to localStorage
              localStorage.setItem(STORAGE_KEY, JSON.stringify({
                type: company.mandatory_theme.type || THEME_TYPES.CUSTOM,
                custom: company.mandatory_theme
              }));

              setIsLoading(false);
              return; // Skip loading user preferences if org theme is enforced
            }
          } catch (error) {
            console.log('[ThemeProvider] Could not fetch company data:', error);
            // Continue to load user theme
          }
        }

        const data = await themeService.getUserTheme();
        console.log('[ThemeProvider] Backend theme data:', data);

        // If data is null, backend is unavailable - use localStorage only
        if (!data) {
          console.log('[ThemeProvider] Backend unavailable - no preference to load');
          setHasBackendPreference(false);
          setIsLoading(false);
          return;
        }

        // Check for admin enforcement first
        if (data.isEnforced && data.enforcedTheme) {
          console.log('[ThemeProvider] Theme is admin-enforced:', data.enforcedTheme.type);
          setIsAdminEnforced(true);
          setEnforcedTheme(data.enforcedTheme);
          setCurrentThemeType(data.enforcedTheme.type);
          setHasBackendPreference(true);

          // Save enforced theme to localStorage
          localStorage.setItem(STORAGE_KEY, JSON.stringify({
            type: data.enforcedTheme.type,
            custom: data.enforcedTheme.type === THEME_TYPES.CUSTOM ? data.enforcedTheme : null
          }));
        } else {
          // Load user preference
          if (data.preference) {
            console.log('[ThemeProvider] User has backend preference:', data.preference);
            setCurrentThemeType(data.preference);
            setHasBackendPreference(true);
          } else {
            console.log('[ThemeProvider] No backend preference found - will show theme selection modal');
            // No backend preference exists for this organization
            setHasBackendPreference(false);
          }
          if (data.customTheme) {
            setCustomTheme(data.customTheme);
          }
        }
      } catch (error) {
        console.log('[ThemeProvider] Error loading backend theme:', error);
        // Backend not available yet - that's okay, we'll use localStorage only
        setHasBackendPreference(false);
      } finally {
        setIsLoading(false);
      }
    };

    loadBackendTheme();
  }, []);

  // Watch for system theme changes (only if user hasn't set preference)
  useEffect(() => {
    if (hasUserSetPreference || isAdminEnforced) {
      return;
    }

    const cleanup = watchSystemPreference((systemTheme) => {
      setCurrentThemeType(systemTheme);
    });

    return cleanup;
  }, [hasUserSetPreference, isAdminEnforced]);

  // Show theme selection modal if no backend preference exists and loading is done
  useEffect(() => {
    // Only show modal if user is logged in
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!isLoading && hasBackendPreference === false && !isAdminEnforced && token) {
      console.log('[ThemeProvider] Showing theme selection modal - no backend preference found');
      setShowThemeSelectionModal(true);
    }
  }, [isLoading, hasBackendPreference, isAdminEnforced]);

  // Get active theme configuration
  const activeTheme = useMemo(() => {
    // Admin enforced theme takes precedence
    if (isAdminEnforced && enforcedTheme) {
      return enforcedTheme;
    }

    // Return appropriate theme based on type
    switch (currentThemeType) {
      case THEME_TYPES.LIGHT:
        return DEFAULT_LIGHT_THEME;
      case THEME_TYPES.CUSTOM:
        return customTheme || EMPTY_CUSTOM_THEME;
      case THEME_TYPES.DARK:
      default:
        return DEFAULT_DARK_THEME;
    }
  }, [currentThemeType, customTheme, isAdminEnforced, enforcedTheme]);

  // Apply theme to DOM whenever it changes
  useEffect(() => {
    if (!isLoading) {
      applyThemeToDOM(activeTheme);
    }
  }, [activeTheme, isLoading]);

  /**
   * Switch to a different theme type
   */
  const switchTheme = useCallback(async (themeType) => {
    if (isAdminEnforced) {
      toast.error('Theme is enforced by administrator');
      return false;
    }

    try {
      setCurrentThemeType(themeType);

      // Mark that user has set a preference
      setHasUserSetPreference(true);
      localStorage.setItem(USER_SET_PREFERENCE_KEY, 'true');

      // Save to localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        type: themeType,
        custom: themeType === THEME_TYPES.CUSTOM ? customTheme : null
      }));

      // Save to backend (optional, fails gracefully if not available)
      try {
        await themeService.saveUserPreference(themeType);
      } catch (error) {
        // Backend not available - theme saved locally only
      }

      toast.success(`Switched to ${themeType} theme`);
      return true;
    } catch (error) {
      console.error('Failed to switch theme:', error);
      toast.error('Failed to save theme preference');
      return false;
    }
  }, [isAdminEnforced, customTheme]);

  /**
   * Update custom theme configuration
   */
  const updateCustomTheme = useCallback(async (updates) => {
    if (isAdminEnforced) {
      toast.error('Theme editing is disabled');
      return false;
    }

    try {
      const updated = {
        ...customTheme,
        ...updates,
        type: THEME_TYPES.CUSTOM,
        editable: true
      };

      setCustomTheme(updated);

      // Save to localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        type: currentThemeType,
        custom: updated
      }));

      // Save to backend (optional, fails gracefully if not available)
      try {
        if (customTheme?.id) {
          await themeService.updateCustomTheme(customTheme.id, updated);
        } else {
          const saved = await themeService.saveCustomTheme(updated);
          setCustomTheme(saved);
        }
      } catch (error) {
        // Backend not available - theme saved locally only
      }

      toast.success('Custom theme saved');
      return true;
    } catch (error) {
      console.error('Failed to update custom theme:', error);
      toast.error('Failed to save custom theme');
      return false;
    }
  }, [customTheme, isAdminEnforced, currentThemeType]);

  /**
   * Reset custom theme to default (dark)
   */
  const resetCustomTheme = useCallback(() => {
    const reset = {
      ...EMPTY_CUSTOM_THEME,
      id: customTheme?.id
    };
    setCustomTheme(reset);
    toast.success('Custom theme reset to default');
  }, [customTheme]);

  /**
   * Delete custom theme
   */
  const deleteCustomTheme = useCallback(async () => {
    if (!customTheme?.id) return false;

    try {
      // Delete from backend (optional, fails gracefully if not available)
      try {
        await themeService.deleteCustomTheme(customTheme.id);
      } catch (error) {
        // Backend not available - deleting locally only
      }

      setCustomTheme(null);

      // Switch to dark theme
      setCurrentThemeType(THEME_TYPES.DARK);
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        type: THEME_TYPES.DARK,
        custom: null
      }));

      toast.success('Custom theme deleted');
      return true;
    } catch (error) {
      console.error('Failed to delete custom theme:', error);
      toast.error('Failed to delete custom theme');
      return false;
    }
  }, [customTheme]);

  /**
   * Check if theme enforcement (admin only)
   */
  const checkEnforcement = useCallback(async () => {
    try {
      const data = await themeService.getEnforcementSettings();
      setIsAdminEnforced(data.enabled);
      if (data.enabled && data.theme) {
        setEnforcedTheme(data.theme);
        setCurrentThemeType(data.theme.type);
      }
      return data;
    } catch (error) {
      console.error('Failed to check theme enforcement:', error);
      return null;
    }
  }, []);

  /**
   * Handle theme selection from modal
   */
  const handleThemeSelection = useCallback(async (themeType) => {
    setCurrentThemeType(themeType);
    setHasUserSetPreference(true);
    setHasBackendPreference(true);
    setShowThemeSelectionModal(false);

    // Save to localStorage
    localStorage.setItem(USER_SET_PREFERENCE_KEY, 'true');
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      type: themeType,
      custom: null
    }));

    // Save to backend (optional, fails gracefully if not available)
    try {
      await themeService.saveUserPreference(themeType);
    } catch (error) {
      // Backend not available - theme saved locally only
    }
  }, []);

  const value = {
    // Current state
    currentThemeType,
    activeTheme,
    customTheme,
    isAdminEnforced,
    enforcedTheme,
    isLoading,
    hasUserSetPreference,

    // Theme types
    themeTypes: THEME_TYPES,

    // Available themes
    availableThemes: [
      { id: THEME_TYPES.DARK, name: 'Dark Mode', theme: DEFAULT_DARK_THEME },
      { id: THEME_TYPES.LIGHT, name: 'Light Mode', theme: DEFAULT_LIGHT_THEME },
      { id: THEME_TYPES.CUSTOM, name: 'Custom Theme', theme: customTheme || EMPTY_CUSTOM_THEME }
    ],

    // Actions
    switchTheme,
    updateCustomTheme,
    resetCustomTheme,
    deleteCustomTheme,
    checkEnforcement,

    // Permissions
    canEditTheme: !isAdminEnforced,
    canSwitchTheme: !isAdminEnforced
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
      <ThemeSelectionModal
        isOpen={showThemeSelectionModal}
        onSelectTheme={handleThemeSelection}
      />
    </ThemeContext.Provider>
  );
}

export default ThemeProvider;
